import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, object=user
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState(null);
  const [perms, setPerms] = useState({ role: null, permissions: [], allowed_company_ids: [] });

  const setActiveWorkspaceId = (id) => {
    if (id) localStorage.setItem("bp_workspace", id);
    setActiveWorkspaceIdState(id);
  };

  const loadWhoAmI = useCallback(async () => {
    try {
      const { data } = await api.get("/whoami");
      setPerms({
        role: data.workspace.role,
        permissions: data.permissions,
        allowed_company_ids: data.workspace.allowed_company_ids,
      });
      return data;
    } catch {
      return null;
    }
  }, []);

  const reloadUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const loadWorkspaces = useCallback(async () => {
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data);
      return data;
    } catch {
      setWorkspaces([]);
      return [];
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        const wsList = await loadWorkspaces();
        const stored = localStorage.getItem("bp_workspace");
        const chosen = stored && wsList.find((w) => w.id === stored) ? stored : (wsList[0]?.id || data.id);
        setActiveWorkspaceIdState(chosen);
        localStorage.setItem("bp_workspace", chosen);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadWorkspaces]);

  useEffect(() => {
    if (activeWorkspaceId) loadWhoAmI();
  }, [activeWorkspaceId, loadWhoAmI]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("bp_token", data.token);
    setUser(data);
    const wsList = await loadWorkspaces();
    const chosen = wsList[0]?.id || data.id;
    setActiveWorkspaceId(chosen);
    return data;
  };

  const register = async (name, email, password, invite_token) => {
    const { data } = await api.post("/auth/register", { name, email, password, invite_token });
    if (data.token) localStorage.setItem("bp_token", data.token);
    setUser(data);
    const wsList = await loadWorkspaces();
    const chosen = wsList[0]?.id || data.id;
    setActiveWorkspaceId(chosen);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("bp_token");
    localStorage.removeItem("bp_workspace");
    setUser(false);
    setWorkspaces([]);
    setActiveWorkspaceIdState(null);
  };

  const has = (perm) => perms.permissions.includes(perm);

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      workspaces, activeWorkspaceId, setActiveWorkspaceId,
      role: perms.role, permissions: perms.permissions, allowed_company_ids: perms.allowed_company_ids,
      has, reloadWhoAmI: loadWhoAmI, reloadWorkspaces: loadWorkspaces, reloadUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
